import { Injectable, InjectionToken, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, Subscription, finalize, firstValueFrom, takeUntil, timeout } from 'rxjs';
import { HubConnection, HubConnectionBuilder, HubConnectionState, HttpTransportType, LogLevel } from '@microsoft/signalr';
import { environment } from '../../environments/environment';
import { CommunicationChange, MessagePage, NotificationPage, NotificationSummary, OrderMessage } from '../models/communication.model';
import { communicationKinds } from '../models/communication.model';
import { communicationHubEvents, communicationTiming } from '../config/communication.config';
export type { AppNotification, CommunicationChange, MessageAttachment, MessagePage, NotificationPage, OrderMessage } from '../models/communication.model';

interface HubTicket { url: string; accessToken: string; expiresAt: string; }
type HubFactory = (url: string, accessTokenFactory: () => Promise<string>) => HubConnection;
export const COMMUNICATION_HUB_FACTORY = new InjectionToken<HubFactory>('SharedCommunicationHubFactory', {
  providedIn: 'root',
  factory: () => (url, accessTokenFactory) => new HubConnectionBuilder()
    .withUrl(url, { accessTokenFactory, withCredentials: false, transport: HttpTransportType.WebSockets })
    .withAutomaticReconnect([...communicationTiming.automaticReconnectDelaysMs]).configureLogging(LogLevel.Error).build()
});

@Injectable({ providedIn: 'root' })
export class CommunicationService {
  private readonly apiBaseUrl = `${environment.apiUrl}/api/communication`;
  private readonly createHub = inject(COMMUNICATION_HUB_FACTORY);
  private hubConnection?: HubConnection;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private isSessionActive = false;
  private isConnecting = false;
  private sessionVersion = 0;
  private connectionTicket?: HubTicket;
  private connectionTicketRequest?: Promise<HubTicket>;
  private readonly sessionEnded = new Subject<void>();
  private summaryRequest?: Subscription;
  private isSummaryLoading = false;
  private isSummaryRefreshQueued = false;
  readonly connected = signal(false);
  readonly unread = signal(0);
  readonly unreadMessageCounts = signal<Record<string, number>>({});
  readonly changes = new Subject<CommunicationChange>();

  constructor(private http: HttpClient) { }

  start(): void {
    if (this.isSessionActive) return;
    this.isSessionActive = true;
    document.addEventListener('visibilitychange', this.handlePageVisibility);
    this.refreshUnread();
    void this.connectToHub(this.sessionVersion);
  }

  stop(): void {
    this.isSessionActive = false;
    this.sessionVersion++;
    this.sessionEnded.next();
    this.summaryRequest?.unsubscribe();
    clearTimeout(this.reconnectTimer);
    document.removeEventListener('visibilitychange', this.handlePageVisibility);
    const hubConnection = this.hubConnection;
    this.hubConnection = undefined;
    void hubConnection?.stop();
    this.connectionTicket = undefined;
    this.connectionTicketRequest = undefined;
    this.isConnecting = false;
    this.isSummaryLoading = false;
    this.isSummaryRefreshQueued = false;
    this.connected.set(false);
    this.unread.set(0);
    this.unreadMessageCounts.set({});
  }

  private readonly handlePageVisibility = () => {
    if (this.isSessionActive && !document.hidden) this.handleCommunicationChange({ kind: communicationKinds.resync });
  };

  private getConnectionTicket(): Promise<HubTicket> {
    if (this.connectionTicket && Date.parse(this.connectionTicket.expiresAt) > Date.now() + communicationTiming.ticketRefreshWindowMs)
      return Promise.resolve(this.connectionTicket);
    if (this.connectionTicketRequest) return this.connectionTicketRequest;
    const sessionVersion = this.sessionVersion;
    const request = firstValueFrom(this.http.get<HubTicket>(`${this.apiBaseUrl}/connection`).pipe(timeout(communicationTiming.requestTimeoutMs), takeUntil(this.sessionEnded)))
      .then(connectionTicket => {
        if (!this.isSessionActive || sessionVersion !== this.sessionVersion) throw new Error('Session ended');
        this.connectionTicket = connectionTicket;
        return connectionTicket;
      }).finally(() => { if (this.connectionTicketRequest === request) this.connectionTicketRequest = undefined; });
    this.connectionTicketRequest = request;
    return request;
  }

  private async connectToHub(sessionVersion: number): Promise<void> {
    if (!this.isSessionActive || sessionVersion !== this.sessionVersion || this.isConnecting) return;
    this.isConnecting = true;
    try {
      const connectionTicket = await this.getConnectionTicket();
      if (!this.isSessionActive || sessionVersion !== this.sessionVersion) return;
      if (!this.hubConnection) {
        const hubConnection = this.createHub(connectionTicket.url, async () => {
          if (!this.isSessionActive || sessionVersion !== this.sessionVersion) throw new Error('Session ended');
          return (await this.getConnectionTicket()).accessToken;
        });
        this.hubConnection = hubConnection;
        this.registerHubHandlers(hubConnection, sessionVersion);
      }
      const hubConnection = this.hubConnection;
      if (hubConnection.state !== HubConnectionState.Disconnected) return;
      await hubConnection.start();
    } catch {
      if (this.isSessionActive && sessionVersion === this.sessionVersion) this.scheduleReconnect(sessionVersion);
    } finally {
      if (sessionVersion === this.sessionVersion) this.isConnecting = false;
    }
  }

  private registerHubHandlers(hubConnection: HubConnection, sessionVersion: number): void {
    // Ready is sent after joining groups; opening a socket alone does not guarantee event delivery yet.
    hubConnection.on(communicationHubEvents.ready, () => {
      if (!this.isCurrentConnection(hubConnection)) return;
      this.connected.set(true);
      this.handleCommunicationChange({ kind: communicationKinds.resync });
    });
    hubConnection.on(communicationHubEvents.changed, (change: CommunicationChange) => {
      if (this.isCurrentConnection(hubConnection)) this.handleCommunicationChange(change);
    });
    hubConnection.onreconnecting(() => {
      if (this.hubConnection === hubConnection) this.connected.set(false);
    });
    hubConnection.onclose(() => {
      if (!this.isCurrentConnection(hubConnection)) return;
      this.connected.set(false);
      this.scheduleReconnect(sessionVersion);
    });
  }

  private isCurrentConnection(hubConnection: HubConnection): boolean {
    return this.isSessionActive && this.hubConnection === hubConnection;
  }

  private scheduleReconnect(sessionVersion: number): void {
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => void this.connectToHub(sessionVersion), communicationTiming.reconnectDelayMs);
  }

  private handleCommunicationChange(change: CommunicationChange): void {
    if (document.hidden) return;
    this.refreshUnread();
    this.changes.next(change);
  }

  refreshUnread(): void {
    if (!this.isSessionActive) return;
    // Preserve one follow-up refresh when an event arrives during an in-flight summary request.
    if (this.isSummaryLoading) { this.isSummaryRefreshQueued = true; return; }
    this.isSummaryLoading = true;
    const sessionVersion = this.sessionVersion;
    this.summaryRequest = this.summary().pipe(timeout(communicationTiming.requestTimeoutMs), finalize(() => {
      if (sessionVersion !== this.sessionVersion) return;
      this.isSummaryLoading = false;
      if (this.isSummaryRefreshQueued) { this.isSummaryRefreshQueued = false; this.refreshUnread(); }
    })).subscribe({
      next: page => {
        if (!this.isSessionActive || sessionVersion !== this.sessionVersion) return;
        this.updateUnreadCounts(page);
      }, error: () => { }
    });
  }

  updateUnreadCounts(summary: NotificationSummary): void {
    this.unread.set(summary.unreadCount);
    this.unreadMessageCounts.set(summary.unreadMessageCounts ?? {});
  }

  inbox(page = 1, unreadOnly = false): Observable<NotificationPage> {
    return this.http.get<NotificationPage>(`${this.apiBaseUrl}/inbox`, { params: { page, unreadOnly } });
  }
  summary(): Observable<NotificationSummary> {
    return this.http.get<NotificationSummary>(`${this.apiBaseUrl}/summary`);
  }
  read(ids: string[]): Observable<void> {
    return this.http.post<void>(`${this.apiBaseUrl}/read`, ids);
  }
  readAll(): Observable<void> {
    return this.http.post<void>(`${this.apiBaseUrl}/read-all`, {});
  }
  messages(orderId: string, before?: string): Observable<MessagePage> {
    return this.http.get<MessagePage>(`${this.apiBaseUrl}/orders/${orderId}/messages`, { params: before ? { before } : {} });
  }
  refreshMessages(orderId: string, ids: string[]): Observable<MessagePage> {
    return this.http.post<MessagePage>(`${this.apiBaseUrl}/orders/${orderId}/messages/refresh`, ids);
  }
  send(orderId: string, id: string, body: string, attachmentIds: string[] = []): Observable<OrderMessage> {
    return this.http.post<OrderMessage>(`${this.apiBaseUrl}/orders/${orderId}/messages`, { id, body, attachmentIds });
  }
  readMessages(orderId: string, ids: string[]): Observable<void> {
    return this.http.post<void>(`${this.apiBaseUrl}/orders/${orderId}/read`, ids);
  }
}
